import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../context/LanguageContext';
import Avatar from '../components/Avatar';
import './ProfilePage.css';
import '../styles/userPages.css';

export default function InboxPage() {
  const { user, profile, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    supabase
      .from('conversations')
      .select(`*, listings(id, title, city), buyer:profiles!conversations_buyer_id_fkey(id, first_name, last_name, full_name, email, avatar_url), agent:profiles!conversations_agent_id_fkey(id, first_name, last_name, full_name, email, avatar_url)`)
      .or(`buyer_id.eq.${user.id},agent_id.eq.${user.id}`)
      .order('last_message_at', { ascending: false })
      .then(({ data }) => {
        setConversations(data || []);
        setLoading(false);
      });
  }, [user?.id]);

  if (!user) { navigate('/login'); return null; }

  const totalUnread = conversations.reduce((sum, c) => {
    if (c.buyer_id === user.id) return sum + (c.buyer_unread || 0);
    if (c.agent_id === user.id) return sum + (c.agent_unread || 0);
    return sum;
  }, 0);
  const unreadLabel = totalUnread === 1
    ? t('inbox.unreadOne')
    : t('inbox.unreadMany').replace('{n}', totalUnread);

  return (
    <div className="profile-page">
      <div className="profile-page__inner">

        <div className="profile-page__sidebar">
          <div className="profile-page__avatar">
            {profile?.is_broker && profile?.partner_status === 'approved' && profile?.avatar_url
              ? <img src={profile.avatar_url} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              : user.avatar}
          </div>
          <div className="profile-page__name">{user.firstName} {user.lastName}</div>
          <div className="profile-page__email">{user.email}</div>
          <nav className="profile-page__nav">
            <button className="profile-page__nav-item" onClick={() => navigate('/profile')}>{t('nav.myProfile')}</button>
            <button className="profile-page__nav-item" onClick={() => navigate('/my-listings')}>{t('nav.myListings')}</button>
            <button className="profile-page__nav-item profile-page__nav-item--active" onClick={() => navigate('/inbox')}>{t('nav.inbox')}</button>
            <button className="profile-page__nav-item" onClick={() => navigate('/settings')}>{t('nav.settings')}</button>
            <button className="profile-page__nav-item profile-page__nav-item--logout" onClick={() => { logout(); navigate('/'); }}>{t('nav.logout')}</button>
          </nav>
        </div>

        <div className="profile-page__content">
          <div className="profile-page__section-header">
            <div className="profile-page__title-row">
              <h1 className="profile-page__title">{t('inbox.title')}</h1>
              {totalUnread > 0 && (
                <span className="profile-page__title-badge" aria-label={unreadLabel}>{totalUnread}</span>
              )}
            </div>
            <p className="profile-page__sub">{t('inbox.sub')}</p>
          </div>

          <div className="profile-page__card">
            {loading ? (
              <div className="profile-page__inbox-state">
                <div className="profile-page__inbox-state-spinner" />
                <span>{t('inbox.loading')}</span>
              </div>
            ) : conversations.length === 0 ? (
              <div className="mylistings__empty">
                <div className="mylistings__empty-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                </div>
                <h3>{t('inbox.empty')}</h3>
                <p>{t('inbox.emptySub')}</p>
                <button className="profile-page__btn" onClick={() => navigate('/listings')}>{t('inbox.browseListings')}</button>
              </div>
            ) : (
              <div className="profile-page__inbox">
                {conversations.map(c => {
                  const isbuyer = c.buyer_id === user.id;
                  const counterpart = isbuyer ? c.agent : c.buyer;
                  const other =
                    counterpart?.full_name?.trim() ||
                    [counterpart?.first_name, counterpart?.last_name].filter(Boolean).join(' ') ||
                    counterpart?.email ||
                    (isbuyer ? 'Agent' : 'Buyer');
                  const unread = isbuyer ? (c.buyer_unread || 0) : (c.agent_unread || 0);
                  return (
                    <div
                      key={c.id}
                      className={`profile-page__inbox-row${unread > 0 ? ' profile-page__inbox-row--unread' : ''}`}
                      onClick={() => navigate(`/notifications?convo=${c.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Avatar avatarUrl={counterpart?.avatar_url} name={other} size={38} />
                      <div className="profile-page__inbox-body">
                        <div className="profile-page__inbox-name">{other}</div>
                        <div className="profile-page__inbox-listing">{c.listings?.title}</div>
                        <div className="profile-page__inbox-preview">{c.last_message || t('nav.noMessagesYet')}</div>
                      </div>
                      {unread > 0 && <span className="profile-page__inbox-badge">{unread}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
